import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { ROUTE_CONFIG } from "@/routes/config";

// The Signalist community node, published to npm — the package name users paste into n8n's
// "Install a community node" dialog (n8n-nodes-signalist).
const N8N_NODE_URL = "https://www.npmjs.com/package/n8n-nodes-signalist";
const N8N_DOCS_URL = "https://docs.signalist.io";

const SETUP_STEP_KEYS = [
	"integrations.n8n.setup.steps.install",
	"integrations.n8n.setup.steps.credentials",
	"integrations.n8n.setup.steps.apiKey",
	"integrations.n8n.setup.steps.build",
] as const;

const SectionHeading: FunctionComponent<{ children: string }> = ({ children }) => (
	<h2 className="text-text text-md font-semibold">{children}</h2>
);

/**
 * Setup guide for the Signalist n8n node: what it is, how to install it, and how to authenticate it
 * with a Signalist API key. Rendered from IntegrationDetail for the `n8n` provider.
 */
export const N8nGuide: FunctionComponent = () => {
	const { t } = useTranslation();

	return (
		<div className="flex max-w-3xl flex-col gap-8">
			<section className="flex flex-col gap-2">
				<SectionHeading>{t("integrations.n8n.overview.title")}</SectionHeading>
				<p className="text-text-meta text-sm leading-relaxed">
					{t("integrations.n8n.overview.body")}
				</p>
			</section>

			<section className="flex flex-col gap-3">
				<SectionHeading>{t("integrations.n8n.setup.title")}</SectionHeading>
				<ol className="flex flex-col gap-2">
					{SETUP_STEP_KEYS.map((key, index) => (
						<li key={key} className="text-text-meta flex items-start gap-3 text-sm leading-relaxed">
							<span className="bg-brand-light text-brand-white mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-medium">
								{index + 1}
							</span>
							<span>{t(key)}</span>
						</li>
					))}
				</ol>
			</section>

			<section className="flex flex-wrap items-center gap-3">
				<Link target="_blank" to={N8N_NODE_URL}>
					<Button
						color={ButtonColor.ACTION}
						image={<Icon className="mr-2" icon={faArrowUpRightFromSquare} />}
						size={ButtonSize.S}
						testId="n8n-node-link"
						title={t("integrations.n8n.setup.openNode")}
					/>
				</Link>
				<Link target="_blank" to={N8N_DOCS_URL}>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.S}
						testId="n8n-docs-link"
						title={t("integrations.n8n.setup.apiDocs")}
					/>
				</Link>
				<Link to={ROUTE_CONFIG.INTEGRATIONS_API}>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.S}
						testId="n8n-api-keys-link"
						title={t("integrations.n8n.setup.manageKeys")}
					/>
				</Link>
			</section>
		</div>
	);
};
